import { useEffect, useState } from 'react';
import { Popover } from 'radix-ui';
import { CalendarClock, Pause, Play, RotateCcw, SkipBack, SkipForward } from 'lucide-react';
import { clockNow, useClock } from '@/state/store';
import { Chip, IconButton, TextButton } from '@/components/ui/primitives';

const SPEEDS = [1, 10, 30, 60, 300, 3600];
const JUMPS: { label: string; ms: number }[] = [
  { label: '-7d', ms: -7 * 86_400_000 },
  { label: '-1d', ms: -86_400_000 },
  { label: '-1h', ms: -3_600_000 },
  { label: '+1h', ms: 3_600_000 },
  { label: '+1d', ms: 86_400_000 },
  { label: '+7d', ms: 7 * 86_400_000 },
];

const p2 = (n: number) => String(n).padStart(2, '0');

function formatDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${p2(d.getUTCMonth() + 1)}-${p2(d.getUTCDate())}`;
}

function formatTime(ms: number): string {
  const d = new Date(ms);
  return `${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())}:${p2(d.getUTCSeconds())}Z`;
}

/** Value for a datetime-local input, in UTC so the picker reads as the clock does. */
function toLocalInput(ms: number): string {
  return new Date(ms).toISOString().slice(0, 16);
}

function speedLabel(s: number): string {
  return s === 1 ? '1x' : s >= 3600 ? `${s / 3600}h/s` : s >= 60 ? `${s / 60}m/s` : `${s}x`;
}

function SpeedSelect({ className }: { className?: string }) {
  const live = useClock((s) => s.live);
  const multiplier = useClock((s) => s.multiplier);
  const setMultiplier = useClock((s) => s.setMultiplier);
  const jump = useClock((s) => s.jump);
  return (
    <select
      aria-label="Playback speed"
      className={className}
      value={multiplier}
      onChange={(e) => {
        if (live) jump(0);
        setMultiplier(Number(e.target.value));
      }}
    >
      {SPEEDS.map((s) => (
        <option key={s} value={s}>
          {speedLabel(s)}
        </option>
      ))}
    </select>
  );
}

/**
 * One clock for everything: live wall time, or a simulated time that plays
 * at a chosen speed. The globe, the twin, the Gantt and the agent read it.
 * Full from xl: play or pause and the speed inline, the hour steps too from
 * 2xl. Compact below xl: play or pause inline, the speed in the time machine
 * popover with the jumps and the date picker. The date stacks over the time
 * until 2xl to keep the bar narrow.
 */
export function TimeConductor({ compact, wide }: { compact: boolean; wide: boolean }) {
  const live = useClock((s) => s.live);
  const replay = useClock((s) => s.playbackRange);
  const playing = useClock((s) => s.playing);
  const setLive = useClock((s) => s.setLive);
  const setPlaying = useClock((s) => s.setPlaying);
  const setSimTime = useClock((s) => s.setSimTime);
  const jump = useClock((s) => s.jump);
  const [now, setNow] = useState(clockNow());
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');

  useEffect(() => {
    const id = window.setInterval(() => setNow(clockNow()), 250);
    return () => window.clearInterval(id);
  }, []);

  const go = () => {
    const ms = Date.parse(draft + ':00Z');
    if (!Number.isFinite(ms)) return;
    setSimTime(ms);
    setOpen(false);
  };

  return (
    <div
      role="group"
      aria-label="Time conductor"
      data-testid="time-conductor"
      data-compact={compact ? '' : undefined}
      className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-hairline bg-inset py-0.5 pl-2 pr-1"
    >
      <Chip tone={live ? 'live' : 'sim'} pulse={live}>
        {replay ? 'REPLAY' : live ? 'LIVE' : 'SIM'}
      </Chip>
      <Popover.Root
        open={open}
        onOpenChange={(o) => {
          if (o) setDraft(toLocalInput(clockNow()));
          setOpen(o);
        }}
      >
        <Popover.Trigger asChild>
          <button
            type="button"
            title="Set the time"
            data-testid="time-machine-trigger"
            className="tabular rounded-control px-1 py-0.5 font-mono text-xs text-ink hover:bg-overlay data-[state=open]:bg-overlay"
          >
            {wide ? (
              `${formatDate(now)} ${formatTime(now)}`
            ) : (
              <span className="flex flex-col items-end leading-[1.15]">
                <span className="text-[10px] text-ink-2">{formatDate(now)}</span>
                <span>{formatTime(now)}</span>
              </span>
            )}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            aria-label="Time machine"
            align="center"
            sideOffset={8}
            collisionPadding={8}
            className="z-50 flex w-[320px] flex-col gap-2 rounded-panel border border-hairline-strong bg-raised p-3 text-xs shadow-elev-2"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-ink-3">
              <CalendarClock aria-hidden className="h-3.5 w-3.5" /> Time machine (UTC)
            </div>
            <div className="flex gap-1" role="group" aria-label="Jump">
              {JUMPS.map((j) => (
                <TextButton key={j.label} onClick={() => jump(j.ms)} className="flex-1 font-mono">
                  {j.label}
                </TextButton>
              ))}
            </div>
            {compact && (
              <label className="flex items-center justify-between gap-2 text-ink-2">
                Speed
                <SpeedSelect className="rounded-control border border-hairline bg-inset px-2 py-1 text-xs text-ink" />
              </label>
            )}
            <div className="flex gap-1">
              <input
                type="datetime-local"
                aria-label="Simulated time"
                value={draft}
                min={replay ? toLocalInput(replay.startMs) : undefined}
                max={replay ? toLocalInput(replay.endMs) : undefined}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') go();
                }}
                className="min-w-0 flex-1 rounded-control border border-hairline bg-inset px-2 py-1 font-mono text-ink"
              />
              <TextButton onClick={go} className="bg-accent-soft text-accent">
                Go
              </TextButton>
              <TextButton
                disabled={Boolean(replay)}
                title={replay ? 'Leave the historical mission to return to live time' : undefined}
                onClick={() => {
                  setLive();
                  setOpen(false);
                }}
              >
                Now
              </TextButton>
            </div>
            <div className="text-ink-3">
              {replay
                ? 'Replay is bounded by launch and splashdown. Leave Artemis to restore your previous clock.'
                : 'Jumps of an hour or more move the analysis window and re-run it.'}
            </div>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
      {wide && (
        <IconButton label="Back one hour" onClick={() => jump(-3600_000)}>
          <SkipBack className="h-3.5 w-3.5" />
        </IconButton>
      )}
      <IconButton
        label={playing ? 'Pause' : 'Play'}
        onClick={() => {
          if (live) jump(0);
          setPlaying(!playing);
        }}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
      </IconButton>
      {wide && (
        <IconButton label="Forward one hour" onClick={() => jump(3600_000)}>
          <SkipForward className="h-3.5 w-3.5" />
        </IconButton>
      )}
      {!compact && (
        <SpeedSelect className="rounded-control border border-hairline bg-raised px-1.5 py-0.5 text-[11px] text-ink-2" />
      )}
      {!live && (
        <IconButton label="Return to live" onClick={setLive}>
          <RotateCcw className="h-3.5 w-3.5" />
        </IconButton>
      )}
    </div>
  );
}
